<template>
  <div class="login-page">
    <div class="form-container">
      <h2>{{ $t("login.title") }}</h2>

      <input
        v-model="username"
        type="text"
        :placeholder="$t('login.usernamePlaceholder')"
      />

      <!-- Обертка для поля пароля и иконки -->
      <div class="password-wrapper">
        <input
          v-model="password"
          :type="passwordFieldType"
          :placeholder="$t('login.passwordPlaceholder')"
        />
        <span class="toggle-password" @click="togglePasswordVisibility">
          <eye-off-outline v-if="isPasswordVisible" :size="24" />
          <eye-outline v-else :size="24" />
        </span>
      </div>

      <div class="buttons">
        <button @click="doLogin" :disabled="loading">
          {{ loading ? $t("login.buttonLoading") : $t("login.button") }}
        </button>
        <button @click="openRegistration" class="register-button">
          {{ $t("login.registerButton") }}
        </button>
      </div>

      <p v-if="error" class="error">{{ error }}</p>

      <div class="forgot-password-container">
        <div @click="openForgotPassword" class="forgot-password-link">
          {{ $t("login.forgotPassword") }}
        </div>
      </div>
    </div>

    <simple-dialog
      v-model="showDialog"
      :title="dialogMessage"
      :success="false"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import SimpleDialog from "@/components/content/SimpleDialog";
// ✅ Импортируем иконки
import EyeOutline from "vue-material-design-icons/EyeOutline";
import EyeOffOutline from "vue-material-design-icons/EyeOffOutline";

export default {
  name: "LoginPassword",
  components: {
    SimpleDialog,
    EyeOutline,
    EyeOffOutline,
  },
  data() {
    return {
      username: "",
      password: "",
      loading: false,
      error: null,
      showDialog: false,
      dialogMessage: "",
      isPasswordVisible: false, // ✅ Новое состояние для видимости пароля
    };
  },
  computed: {
    // ✅ Вычисляемое свойство для типа поля
    passwordFieldType() {
      return this.isPasswordVisible ? "text" : "password";
    },
  },
  methods: {
    ...mapActions("Auth", ["loginWithBills"]),
    // ✅ Метод для переключения видимости
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible;
    },
    /**
     * ✅ ОБНОВЛЕНО: Авторизация с возможностью указать счет
     */
    async doLogin() {
      this.error = null;

      if (!this.username || !this.password) {
        this.dialogMessage = this.$t("login.errors.fillAllFields");
        this.showDialog = true;
        return;
      }

      this.loading = true;
      try {
        // Проверяем, есть ли в URL параметр account_id
        let account_id = null;
        if (this.$route.query && this.$route.query.account_id) {
          account_id = this.$route.query.account_id;
          console.log("Найден account_id в URL:", account_id);
        }

        // Также проверяем localStorage на наличие lastAccount
        if (!account_id) {
          try {
            const lastAccountData = localStorage.getItem("lastAccount");
            if (lastAccountData) {
              const lastAccount = JSON.parse(lastAccountData);
              if (
                lastAccount &&
                (lastAccount.id || lastAccount.accountNumber || lastAccount.ls)
              ) {
                account_id =
                  lastAccount.id || lastAccount.accountNumber || lastAccount.ls;
                console.log("Найден account_id в localStorage:", account_id);
              }
            }
          } catch (parseError) {
            console.error("Ошибка при получении lastAccount:", parseError);
          }
        }

        // Формируем параметры для входа
        const loginParams = {
          username: this.username,
          password: this.password,
        };

        // Добавляем account_id если он есть
        if (account_id) {
          loginParams.account_id = account_id;
          console.log("Добавлен account_id в параметры входа:", account_id);
        }

        const response = await this.loginWithBills(loginParams);

        // Проверка на ошибку "Відсутні рахунки для цього користувача" - ПЕРЕД другими ошибками
        if (
          response &&
          response.status === "error" &&
          response.message &&
          response.message.includes("Відсутні рахунки")
        ) {
          // Пользователь авторизован, но у него нет счетов - перенаправляем на добавление
          this.$router.push({ name: "AddAccount" });
          return;
        }

        // Проверка на другие ошибки (например, "Невірний логін або пароль")
        if (response && response.status === "error") {
          this.error =
            response.message || this.$t("login.errors.invalidCredentials");
          this.loading = false;
          return; // Останавливаем выполнение и не переходим никуда
        }

        if (response && response.bills && response.bills.length === 0) {
          this.$router.push({ name: "AddAccount" });
        } else {
          this.$router.push({ name: "AccountsListPage" });
        }
      } catch (e) {
        // Проверка на ошибку "Відсутні рахунки для цього користувача" в catch - ПЕРВАЯ проверка
        if (e && e.message && e.message.includes("Відсутні рахунки")) {
          // Пользователь авторизован, но у него нет счетов - перенаправляем на добавление
          this.$router.push({ name: "AddAccount" });
          return;
        }

        if (e && e.status === "fail") {
          this.error = this.$t("login.errors.invalidCredentials");
        } else if (e && e.status === "error" && e.message) {
          // Обработка других ошибок с сервера с явным статусом error
          this.error = e.message;
        } else if (e.response) {
          this.error = `${this.$t("login.errors.serverError")}: ${
            e.response.data?.message || e.message
          }`;
        } else {
          this.error = `${this.$t("login.errors.authError")}: ${
            e.message || "неизвестная ошибка"
          }`;
        }
      } finally {
        this.loading = false;
      }
    },
    openRegistration() {
      this.nativeSendEvent("url", "https://pay.elektro.volyn.ua/user/register");
    },
    openForgotPassword() {
      this.nativeSendEvent("url", "https://pay.elektro.volyn.ua/user/password");
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.login-page {
  color: #ecf0f1;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 15vh;
}

.form-container {
  width: 100%;
  max-width: 400px;
  text-align: center;
  padding: 0 30px;
}

h2 {
  color: #ffffff;
  margin-bottom: 30px;
  font-size: 22px; /* <-- Вот здесь можно менять размер */
}

/* ✅ Стили для обертки пароля */
.password-wrapper {
  position: relative;
  width: 100%;
  margin: 15px 0;
}

.password-wrapper input {
  margin: 0; /* Убираем margin у инпута внутри обертки */
}

/* ✅ Стили для иконки глаза */
.toggle-password {
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #95a5a6;
}

.toggle-password:hover {
  color: #ecf0f1;
}

input {
  background-color: #2c3e50;
  color: #ecf0f1;
  border: 1px solid #34495e;
  display: block;
  width: 100%;
  margin: 15px 0;
  padding: 12px;
  font-size: 16px;
  border-radius: 8px;
  /* Добавляем padding-right, чтобы текст не заходил под иконку */
  padding-right: 50px;
}

input::placeholder {
  color: #95a5a6;
}

.buttons {
  width: 100%;
  margin-top: 25px;
}

button {
  display: block;
  width: 100%;
  margin: 10px 0;
  padding: 12px;
  cursor: pointer;
  font-size: 16px;
  background-color: #4277f5;
  color: white;
  border: none;
  border-radius: 8px;
  transition: background-color 0.3s;
  font-weight: bold;
}

button:disabled {
  background-color: #566573;
  cursor: not-allowed;
}

button:hover:not(:disabled) {
  background-color: #4277f5;
}

.register-button {
  display: block;
  width: 100%;
  margin: 10px 0;
  padding: 12px;
  cursor: pointer;
  font-size: 16px;
  background-color: transparent;
  border: 1px solid #4277f5;
  color: #4277f5;
  border-radius: 8px;
  transition: background-color 0.3s;
  font-weight: bold;
  text-decoration: none;
  text-align: center;
  box-sizing: border-box;
}

.register-button:hover {
  background-color: rgba(52, 152, 219, 0.1);
}

.error {
  color: #e74c3c;
  margin-top: 15px;
  padding: 10px;
  border: 1px solid #e74c3c;
  background-color: rgba(231, 76, 60, 0.1);
  border-radius: 8px;
  font-weight: bold;
}

.forgot-password-container {
  margin-top: 20px;
  text-align: center;
}

.forgot-password-link {
  color: #4277f5;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s;
}

.forgot-password-link:hover {
  color: #3498db;
  text-decoration: underline;
}
</style>
